import axios from "axios";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import ReCAPTCHA from "react-google-recaptcha";
import { useTranslation } from "react-i18next";
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  useToast,
  Text,
  Heading,
  VStack,
  Flex,
} from "@chakra-ui/react";

export default function ContactForm() {
  const colors = {
    teal: "#1da179",
    tealLight: "#34c49b",
    red: "#f21d1b",
    orangeRed: "#f31f1c",
  };

  const toast = useToast();
  const [captchaToken, setCaptchaToken] = useState("");
  const { i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm();

  const onSubmit = async (data) => {
    try {
      await axios.post(
        "https://actpolymers-spring-26d90d713ec5.herokuapp.com/api/contact",
        {
          ...data,
          captchaToken,
        }
      );

      toast({
        title: isArabic ? "تم الإرسال بنجاح" : "Submitted Successfully",
        description: isArabic
          ? "سنتواصل معك قريبًا."
          : "We'll get back to you soon.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      reset();
      setCaptchaToken("");
    } catch (error) {
      toast({
        title: isArabic ? "فشل الإرسال" : "Submission Failed",
        description: isArabic
          ? "حدث خطأ أثناء الإرسال، حاول مرة أخرى لاحقًا."
          : "Please try again later.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <Box
      bgGradient={`linear(to-r, ${colors.teal}, ${colors.tealLight})`}
      py={12}
      px={4}
    >
      {/* Header */}
      <Heading
        textAlign="center"
        mb={8}
        fontSize="2xl"
        color="white"
        fontWeight="extrabold"
      >
        {isArabic ? "تواصل معنا" : "Get in Touch"}
      </Heading>

      <Flex
        direction={{ base: "column", md: "row" }}
        gap={6}
        maxW="6xl"
        mx="auto"
        align="stretch"
      >
        {/* FORM */}
        <Box
          flex="1"
          bg="whiteAlpha.900"
          backdropFilter="blur(10px)"
          p={{ base: 4, md: 6 }}
          borderRadius="xl"
          boxShadow="lg"
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <VStack spacing={4}>
              <FormControl isInvalid={errors.name}>
                <FormLabel fontSize="sm" color="gray.700">
                  {isArabic ? "الاسم" : "Name"}
                </FormLabel>
                <Input
                  {...register("name", { required: "Name is required" })}
                  placeholder={isArabic ? "اسمك" : "Your Name"}
                  size="sm"
                  borderRadius="full"
                  focusBorderColor={colors.teal}
                />
                {errors.name && (
                  <Text color="red.500" fontSize="xs">
                    {errors.name.message}
                  </Text>
                )}
              </FormControl>

              <FormControl isInvalid={errors.phone}>
                <FormLabel fontSize="sm" color="gray.700">
                  {isArabic ? "رقم الجوال" : "Phone Number"}
                </FormLabel>
                <Input
                  {...register("phone", { required: "Phone number is required" })}
                  placeholder={isArabic ? "رقم الجوال" : "Your Phone"}
                  size="sm"
                  borderRadius="full"
                  focusBorderColor={colors.teal}
                />
                {errors.phone && (
                  <Text color="red.500" fontSize="xs">
                    {errors.phone.message}
                  </Text>
                )}
              </FormControl>

              <FormControl isInvalid={errors.email}>
                <FormLabel fontSize="sm" color="gray.700">
                  {isArabic ? "البريد الإلكتروني" : "Email"}
                </FormLabel>
                <Input
                  {...register("email", { required: "Email is required" })}
                  type="email"
                  placeholder="name@example.com"
                  size="sm"
                  borderRadius="full"
                  focusBorderColor={colors.teal}
                />
                {errors.email && (
                  <Text color="red.500" fontSize="xs">
                    {errors.email.message}
                  </Text>
                )}
              </FormControl>

              <FormControl isInvalid={errors.message}>
                <FormLabel fontSize="sm" color="gray.700">
                  {isArabic ? "الرسالة" : "Message"}
                </FormLabel>
                <Textarea
                  {...register("message", { required: "Message is required" })}
                  placeholder={isArabic ? "اكتب رسالتك" : "Write your message..."}
                  size="sm"
                  borderRadius="md"
                  rows={4}
                  focusBorderColor={colors.teal}
                />
                {errors.message && (
                  <Text color="red.500" fontSize="xs">
                    {errors.message.message}
                  </Text>
                )}
              </FormControl>

              {/* 
              <Box mt={1} w="full" transform="scale(0.9)" transformOrigin="left">
                <ReCAPTCHA
                  sitekey="6Ldat88rAAAAANDbsRBRqgBCkoAggJBLneI1xVR2"
                  onChange={(token) => setCaptchaToken(token)}
                />
              </Box> 
              */}

              <Button
                type="submit"
                size="md"
                borderRadius="full"
                px={6}
                fontWeight="semibold"
                bgGradient={`linear(to-r, ${colors.teal}, ${colors.tealLight})`}
                color="white"
                isLoading={isSubmitting}
                width="full"
                _hover={{
                  shadow: "md",
                }}
              >
                {isArabic ? "إرسال" : "Submit"}
              </Button>
            </VStack>
          </form>
        </Box>

        {/* MAP */}
        <Box
          flex="1"
          borderRadius="xl"
          overflow="hidden"
          boxShadow="lg"
        >
          <iframe
            title="Company Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1634.0726041473322!2d45.793211018748835!3d25.63155741814776!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e2ba77e70dc4831%3A0x3c2f0f7b695e5d87!2sActive%20Polymers%20Factory!5e1!3m2!1sen!2ssa!4v1758526957761!5m2!1sen!2ssa"
            width="100%"
            height="100%"
            style={{ border: 0, minHeight: "100%" }}
            allowFullScreen=""
            referrerPolicy="no-referrer-when-downgrade"
          />
        </Box>
      </Flex>
    </Box>
  );
}
